// index.tsx
import * as React from 'react';
import { Box, Grid, Button, FormGroup, TextField, TextareaAutosize, FormControl, FormHelperText, InputLabel } from '@mui/material';
import { PageProps } from '../../types/navigationProperties';
import { displayErrorToast, displayMessageToast } from '../../utils/toasts';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import Footer from '../../components/Footer';
import { Email } from '../../types/email';
import segedaContact from '../../assets/img/segeda_contact.png';
import { sendEmailContact, validateEmail } from '../../services/EmailsServiceProvider';
import { CompanyLocation, getCompanyLocation } from '../../services/CompaniesServiceProvider';
import { LightShotSingle } from '../../components/LightShotSingle';
import { GalleryImage } from '../../types/galleryImage';
import { useTheme } from '@mui/material/styles';
import { SetButtonCallbackFunction } from '../../types/callback';
import { PaginatedResponse } from '../../types/pagination';
import { Company } from '../../services/CompaniesServiceProvider';
import { SEO } from '../../components/SEO';
import { MetaTag } from '../../services/SEOServiceProvider';
import LoaderSpinner from '../../components/LoaderSpinner';

export default function ContactUsDetails({ props, setPageName, metaTags }: { props: PageProps, setPageName: SetButtonCallbackFunction, metaTags: MetaTag[] }) {
    const language = props.language;
    const companies: PaginatedResponse<Company> | undefined = props.companies;
    const theme = useTheme();
    const { t } = useTranslation();
    const [sending, setSending] = React.useState<boolean>(false);
    const [email, setEmail] = React.useState<Email>({
        name: '',
        email: '',
        message: '',
    });
    const [formState, setFormState] = React.useState(() => ({
        errors:{
            name: '',
            email: '',
            message: '',
        }
    }));
    const formErrors = formState.errors;
    const [open, setOpen] = React.useState(false);
    const [selectedImage, setSelectedImage] = React.useState<GalleryImage>();
    const [metaTag, setMetaTag] = React.useState<MetaTag>();
    const [map, setMap] = React.useState<CompanyLocation>();
    const [loading, setLoading] = React.useState<boolean>();

    const setFormErrors = (newFormErrors: any) => {
        setFormState((previousState) => ({
            ...previousState,
            errors: newFormErrors,
        }));
    };

    React.useEffect(() => {
        setPageName('contact-us');
        let metaTagData = metaTags !== undefined && metaTags.filter((item: MetaTag) => item.page === 'contact-us' );
        if(metaTagData && metaTagData.length > 0) {
            let contactPageMetaTag: MetaTag = {...metaTagData[0]} as MetaTag;
            contactPageMetaTag.url = window.location.href;
            contactPageMetaTag.image = segedaContact;
            setMetaTag(contactPageMetaTag);
        }
        if(companies !== undefined && companies.results.length > 0) {
            (async () => {
                try {
                    let popup = language === 'en' ? t("contact.map.popup") : t("contact.map.popup");
                    setLoading(true);
                    const companyLocationData = await getCompanyLocation(companies.results.length > 0 ? companies.results[0].email : '', popup);
                    console.log(companyLocationData);
                    console.log(popup);
                    setMap(companyLocationData);
                } catch(error) {
                    displayErrorToast(error);
                } finally {
                    setLoading(false);
                }
            })();
        }

    }, [setPageName, metaTags, language, companies, t]);

    const headingProps = {
        title: t("contact.title"),
        description: undefined
    };

    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setEmail((prev) => ({
            ...email,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        const { issues } = validateEmail(email);
        if (issues && issues.length > 0) {
            setFormErrors(
                Object.fromEntries(issues.map((issue: any) => [issue.path?.[0], issue.message])),
            );
            return;
        } else {
            setFormErrors({});
            try {
                setSending(true);
                const sendEmailData = await sendEmailContact(email);
                console.log(sendEmailData);
                displayMessageToast(`${t('contact.sent.success')}!`);
                handleReset();
            } catch(error) {
                displayErrorToast(error);
            } finally {
                setSending(false);
            }
        }
    };

    const handleReset = () => {
        setEmail({
            name: '',
            email: '',
            message: '',
        });
        setFormErrors({});
    }

    const handleOpenLightShot = (image: string, name: string) => {
        let item: GalleryImage = { src: image, name: name };
        setSelectedImage(item);
        setOpen(true);
    }

    return loading ? (
        <div>
            <SEO metaTag={metaTag} />
            <LoaderSpinner loading={loading} />
        </div>
    ) : (
        <Box>
            <SEO metaTag={metaTag} />
            <LightShotSingle image={selectedImage} opened={open} setOpenLightShot={setOpen} />
            <Box>
                <PageHeader props={headingProps} />
            </Box>
            <Box sx={{ py: '40px', px: '20px', paddingTop: { xs: '0px', sm: '0px', md: '40px' }, bgcolor: 'primary.light' }}
                 style={{ width: '100%', height: '100%' }}>
                    <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
                        <Grid size={{ xs: 12, sm: 12, md: 6 }} sx={{ display: 'flex' }}>
                            <img loading="lazy" src={segedaContact}
                            style={{
                                position: 'relative',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                maxWidth: '500px',
                                width: '100%',
                            }}
                            alt="segeda project" onClick={() => handleOpenLightShot(segedaContact, "segeda project")} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 6 }} sx={{
                                    display: 'flex',
                                    position: 'relative',
                                    marginTop: 'auto',
                                    marginBottom: 'auto',
                                    height: '100%',
                                    paddingTop: { xs: '40px', sm: '40px', md: '0px' }
                                }}>
                            <Grid size={{ xs: 12, sm: 12, md: 8, lg: 8 }}>
                                <Grid>
                                    <Box component="form"
                                        noValidate
                                        autoComplete="off"
                                        sx={{ width: '100%' }}>
                                        <FormGroup>
                                            <Grid container spacing={4} sx={{ mb: 2, width: '100%' }}>
                                                <Grid size={{ xs: 12, sm: 12 }} sx={{ display: 'flex' }}>
                                                    <FormControl fullWidth>
                                                        <InputLabel id="email-name">{t('contact.form.name')}</InputLabel>
                                                        <TextField id="email-name"
                                                            value={email.name ?? ''}
                                                            onChange={handleValueChange}
                                                            name="name"
                                                            size="medium"
                                                            placeholder={t('input.placeholder.name') + ' ' + t('contact.form.name')}
                                                            fullWidth
                                                            variant="outlined"
                                                            color={!!formErrors.name ? 'error' : 'primary'}/>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 12 }} sx={{ display: 'flex' }}>
                                                    <FormControl error={!!formErrors.email} fullWidth>
                                                        <InputLabel id="email-label">{t('contact.form.email')}</InputLabel>
                                                        <TextField id="email-label"
                                                            value={email.email ?? ''}
                                                            onChange={handleValueChange}
                                                            name="email"
                                                            size="medium"
                                                            placeholder={t('input.placeholder.email') + ' ' + t('contact.form.email')}
                                                            fullWidth
                                                            variant="outlined"
                                                            color={!!formErrors.email ? 'error' : 'primary'}/>
                                                        <FormHelperText>{formErrors.email ?? ' '}</FormHelperText>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 12 }} sx={{ display: 'flex' }}>
                                                    <FormControl error={!!formErrors.message} fullWidth>
                                                        <InputLabel id="email-message-label">{t('contact.form.message')}</InputLabel>
                                                        <TextareaAutosize id="email-message-label"
                                                            value={email.message ?? ''}
                                                            onChange={handleValueChange}
                                                            name="message"
                                                            minRows={5}
                                                            style={{
                                                                marginTop: '6px',
                                                                fontFamily: 'Oswald, sans-serif',
                                                                fontWeight: 400,
                                                                fontSize: '0.875rem',
                                                                lineHeight: '1.4375em',
                                                                padding: '8px 12px',
                                                                color: (theme.vars || theme).palette.text.primary,
                                                                borderRadius: (theme.vars || theme).shape.borderRadius,
                                                                border: `1px solid ${(theme.vars || theme).palette.divider}`,
                                                                backgroundColor: (theme.vars || theme).palette.background.default,
                                                            }}
                                                            placeholder={t('input.placeholder.message') + ' ' + t('contact.form.message')}
                                                            color={!!formErrors.message ? 'error' : 'primary'}/>
                                                        <FormHelperText>{formErrors.message ?? ' '}</FormHelperText>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                                <Box>
                                                    <Button variant="outlined"
                                                        color="secondary"
                                                        size="large"
                                                        style={{ width: '200px'}}
                                                        onClick={handleSubmit}
                                                        loading={sending}>
                                                        {t('contact.form.submit')}
                                                    </Button>
                                                </Box>
                                        </FormGroup>
                                    </Box>
                                    {/*map !== undefined ? (
                                        <Box sx={{ paddingTop: '20px' }}>
                                            <iframe loading="lazy"
                                                frameBorder="0"
                                                title="location"
                                                style={{ width: '100%', minHeight: '270px' }}
                                                srcDoc={map.map} />
                                        </Box>
                                    ) : (
                                        <></>
                                    )*/}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
            </Box>
            <Footer {...props} />
        </Box>
    );
}
